import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { cartService } from "@/api/cartService"
import { toast } from "sonner"

export const useCart = () => {
  const queryClient = useQueryClient()

  const token =
    typeof window !== "undefined" ? localStorage.getItem("website_token") : null

  const {
    data,
    isLoading,
    error,
    refetch: fetchCart,
  } = useQuery({
    queryKey: ["cart"],
    queryFn: ({ signal }) => cartService.getCart(signal),
    enabled: !!token, // Only fetch if user is logged in
  })

  const addItemMutation = useMutation({
    mutationFn: cartService.addItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      toast.success("Item added to cart")
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to add item to cart")
    },
  })

  const removeItemMutation = useMutation({
    mutationFn: cartService.removeItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      toast.success("Item removed from cart")
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to remove item")
    },
  })

  const updateQuantityMutation = useMutation({
    mutationFn: ({
      cartItemId,
      quantity,
    }: {
      cartItemId: number
      quantity: number
    }) => cartService.updateQuantity(cartItemId, { quantity }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] })
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to update quantity")
    },
  })

  const clearCartMutation = useMutation({
    mutationFn: cartService.clear,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      toast.success("Cart cleared")
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to clear cart")
    },
  })

  const checkoutMutation = useMutation({
    mutationFn: cartService.checkout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      queryClient.invalidateQueries({ queryKey: ["orders"] })
      queryClient.invalidateQueries({ queryKey: ["website-product"] })
      queryClient.invalidateQueries({ queryKey: ["website-products"] })
      toast.success("Order placed successfully")
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to checkout")
    },
  })

  return {
    cart: (data as any)?.data,
    isLoading,
    error,
    fetchCart,
    addItem: addItemMutation.mutate,
    removeItem: removeItemMutation.mutate,
    updateQuantity: updateQuantityMutation.mutate,
    updateQuantityAsync: updateQuantityMutation.mutateAsync,
    clearCart: clearCartMutation.mutate,
    checkout: checkoutMutation.mutateAsync,
    isAdding: addItemMutation.isPending,
    isRemoving: removeItemMutation.isPending,
    isUpdating: updateQuantityMutation.isPending,
    isClearing: clearCartMutation.isPending,
    isCheckingOut: checkoutMutation.isPending,
  }
}
